import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Wand2, ShieldAlert, Loader2, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { streamContractAI } from "../../lib/contractsApi";

const MODES = [
  { k: "draft", l: "صياغة عقد", icon: Sparkles, ph: "مثال: تأجير سيارة هيونداي إلنترا 2024 لمدة شهر بمبلغ 2500 ريال، وتأمين 1000 ريال، والحد اليومي 300 كم..." },
  { k: "edit", l: "تعديل بند", icon: Wand2, ph: "مثال: أضف بنداً للشرط الجزائي عند التأخير بـ 200 ريال يومياً، واجعل مدة الإشعار بالإنهاء 15 يوماً" },
  { k: "review", l: "مراجعة قانونية", icon: ShieldAlert, ph: "اختياري: ركّز على حماية حقوق الشركة..." },
];

// AI contract agent (Claude): draft / edit / legal review. Applies output to the editor via onApply.
export const ContractAI = ({ form, getBody, onApply }) => {
  const [mode, setMode] = useState("draft");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState("");
  const m = MODES.find((x) => x.k === mode);
  const run = async () => {
    setBusy(true); setOut("");
    try {
      const html = await streamContractAI({
        mode, prompt, type: form.type, body_html: getBody(),
        party: { name: form.party_name, id_number: form.party_id_number, phone: form.party_phone, start: form.start_date, end: form.end_date, value: form.value },
      }, setOut);
      setOut(html);
      if (mode !== "review") { onApply(html); toast.success(mode === "draft" ? "تمت صياغة العقد — راجعه وعدّل ما تريد" : "تم تطبيق التعديل"); }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-violet-200 bg-gradient-to-b from-violet-50 to-white p-3 space-y-2" data-testid="contract-ai">
      <p className="text-sm font-bold text-violet-800 flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> وكيل العقود الذكي</p>
      <div className="grid grid-cols-3 gap-1">
        {MODES.map((x) => (
          <button key={x.k} type="button" onClick={() => setMode(x.k)} data-testid={`contract-ai-mode-${x.k}`}
            className={`rounded-lg px-1 py-1.5 text-[11px] font-semibold flex flex-col items-center gap-0.5 border transition-colors ${mode === x.k ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>
            <x.icon className="h-3.5 w-3.5" />{x.l}
          </button>
        ))}
      </div>
      <Textarea rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={m.ph} className="text-sm bg-white" data-testid="contract-ai-prompt" />
      <Button onClick={run} disabled={busy} className="w-full gap-2 bg-violet-600 hover:bg-violet-700" data-testid="contract-ai-run">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <m.icon className="h-4 w-4" />} {busy ? "جارٍ الكتابة..." : m.l}
      </Button>
      {out && (
        <div className="rounded-xl border bg-white p-2 max-h-72 overflow-y-auto text-xs leading-6 text-slate-700 [&_h3]:font-bold [&_h3]:mt-2 [&_ul]:list-disc [&_ul]:pr-4 [&_ol]:list-decimal [&_ol]:pr-4" data-testid="contract-ai-output"
          dangerouslySetInnerHTML={{ __html: out.replace(/<script[\s\S]*?<\/script>/gi, "") }} />
      )}
      {out && !busy && mode === "review" && <p className="text-[11px] text-slate-500 flex items-center gap-1"><Check className="h-3 w-3" /> استخدم «تعديل بند» لتطبيق أي اقتراح.</p>}
    </div>
  );
};
